<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Layout from '@layouts/login'
import localSave from '@utils/local-save'
import { useApi } from '@composables/use-api'
import { required, sameAs, useFormValidation } from '@composables/use-form-validation'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMemberStore } from '@state/stores/member'
import { useMessages } from '@composables/use-messages'

const getMessageText = useMessages()
const route = useRoute()
const router = useRouter()
const appearanceStore = useAppearanceStore()
const memberStore = useMemberStore()
const member = reactive({ id: '', password: '' })
const confirmPassword = ref('')
const checking = ref(false)
const membersId = computed(() => Number(route.query.members_id))
const token = computed(() => route.query.token)
const memberEmail = computed(() => memberStore.currentMember?.email || null)
const validation = useFormValidation({
    password: { value: computed(() => member.password), validators: [required] },
    confirmPassword: {
        value: confirmPassword,
        validators: [required, sameAs(() => member.password)],
    },
})

function notify(messageKey, type) {
    appearanceStore.handleNotification({
        message: getMessageText(messageKey),
        type,
    })
}

function redirect() {
    router.push({ path: localSave.get('redirectURL') || '/' })
}

function handleSuccess() {
    checking.value = false
    notify('NEW_PASSWORD_SUCCESS', 'success')
    redirect()
}

function handleError() {
    checking.value = false
    notify('SOMETHING_WENT_WRONG', 'danger')
}

function setNewPassword() {
    if (validation.$invalid) {
        checking.value = false
        notify('FIELDS_REQUIRED', 'warning')
        validation.$reset()
        return undefined
    }

    checking.value = true
    member.id = membersId.value
    return useApi()
        .members.put({ id: member.id }, member, {
            headers: { Authorization: `Bearer ${token.value}` },
        })
        .then(handleSuccess)
        .catch(handleError)
}

defineOptions({ name: 'SetNewPassword' })

usePageHead({ title: 'Set new password', description: 'Set new password' })
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <div class="set-password-form-container">
                    <h4 class="set-password-title">Reset Password</h4>
                    <h6 class="set-password-subtitle">Reset your password below</h6>
                    <form
                        class="set-password-form"
                        @submit.prevent="setNewPassword"
                    >
                        <div
                            v-if="memberEmail"
                            class="set-password-field-container"
                        >
                            <BaseInput
                                v-model="memberEmail"
                                :class="['set-password-field', 'set-password-email']"
                                type="email"
                                disabled
                            />
                        </div>
                        <div class="set-password-field-container">
                            <BaseInput
                                v-model="member.password"
                                :class="[
                                    'set-password-field',
                                    validation.password.$error && 'set-password-error',
                                ]"
                                type="password"
                                placeholder="password*"
                                @blur="validation.password.$touch()"
                            />
                            <label
                                v-if="validation.password.$error"
                                :class="['set-password-label', 'set-password-error']"
                            >This field is required</label>
                        </div>
                        <div class="set-password-field-container">
                            <BaseInput
                                v-model="confirmPassword"
                                :class="[
                                    'set-password-field',
                                    validation.confirmPassword.$error && 'set-password-error',
                                ]"
                                type="password"
                                placeholder="Confirm password*"
                                @blur="validation.confirmPassword.$touch()"
                            />
                            <label
                                v-if="validation.confirmPassword.$error"
                                :class="['set-password-label', 'set-password-error']"
                            >This field is required and should match password above</label>
                        </div>
                        <BaseButton
                            class="set-password-button"
                            :disabled="checking"
                            variant="primary"
                            fill
                            type="submit"
                            @click="() => {}"
                        >Reset Password</BaseButton>
                    </form>
                </div>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.set-password-form-container {
    position: relative;
    margin-bottom: $s-h;
    .set-password-field {
        &.set-password-error {
            margin-bottom: $s-xl;
            border-color: $c-status-error-text;
            border-width: 2px;
        }
    }
}
.set-password-field-container {
    position: relative;
    width: 100%;
}
.set-password-form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.set-password-label {
    @extend %contentSubtitleM;

    display: block;
    font-weight: 300;
    text-align: left;

    &.set-password-error {
        position: absolute;
        bottom: $s-xs;
        color: $c-status-error-text;
    }
}
.set-password-title {
    text-align: left;
}
.set-password-subtitle {
    @extend %contentTinyHeading;

    font-weight: 400;
    text-align: left;
}

.set-password-button {
    margin-top: $s-xl;
}

.set-password-email {
    color: $c-accent-pressed;
}
</style>
